import { useCallback, useMemo, useState } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { FlowTitle } from '@/components/ui/text';
import { ArrowLeftIcon, ArrowRightIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { StepIndicator } from './StepIndicator';
import { ChoosePlatformStep } from './steps/ChoosePlatformStep';
import { useStoryVisibilityChoice } from './useStoryVisibilityChoice';
import { ProjectInfoStep } from './steps/ProjectInfoStep';
import { SelectScenesStep } from './steps/SelectScenesStep';
import { ProjectStoriesStep } from './steps/ProjectStoriesStep';
import { ProjectConfirmationStep } from './steps/ProjectConfirmationStep';
import { usePublishFlowImages } from './usePublishFlowImages';
import {
  PROJECT_ALL_STEPS,
  PROJECT_STEP_LABELS,
  useProjectPublishFlow,
} from './useProjectPublishFlow';
import type { SceneCardStatus, SceneCardValues } from './SceneCard';
import {
  getMissingFields,
  isEmailLike,
  isValidStoryName,
  withoutKeys,
  type FieldKey,
  type PublishFlowValues,
} from './validation';
import type {
  ProjectPublishSubmitArgs,
  ProjectSceneTarget,
  ProjectStepId,
} from './types';

interface ProjectPublishFlowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectInstanceId: string;
  scenes: ProjectSceneTarget[];
  creatorName: string;
  initialProjectName?: string;
  initialProjectDescription?: string;
  initialProjectTags?: string[];
  initialSceneIntro?: Record<string, string>;
  onReorderScenes: (orderedSceneOriginIds: string[]) => void;
  onSubmit: (args: ProjectPublishSubmitArgs) => Promise<void>;
}

const STEP_TITLES: Record<ProjectStepId, string> = {
  'select-scenes': 'Choose stories to publish',
  'choose-platform': 'Choose where to publish',
  'project-info': 'Project info',
  stories: 'Add story details',
  confirmation: 'Confirm and submit',
};

function latestSelectableVersion(scene: ProjectSceneTarget): string | undefined {
  // Place-anywhere scenes have no anchor and can't be published, so they're never
  // auto-selected or toggleable in the project flow.
  return scene.versions.find((v) => v.selectable && v.anchorCategory !== 'place_anywhere')?.sceneInstanceId;
}

export function ProjectPublishFlowModal({
  open,
  onOpenChange,
  projectInstanceId,
  scenes,
  creatorName: initialCreatorName,
  initialProjectName = '',
  initialProjectDescription = '',
  initialProjectTags = [],
  initialSceneIntro = {},
  onReorderScenes,
  onSubmit,
}: ProjectPublishFlowModalProps) {
  const flow = useProjectPublishFlow();

  const seededCreatorName = isEmailLike(initialCreatorName) ? '' : initialCreatorName;

  const buildDefaultSelection = useCallback((): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const scene of scenes) {
      const v = latestSelectableVersion(scene);
      if (v) next[scene.sceneOriginId] = v;
    }
    return next;
  }, [scenes]);

  const buildDefaultSceneValues = useCallback((): Record<string, SceneCardValues> => {
    const next: Record<string, SceneCardValues> = {};
    for (const scene of scenes) {
      next[scene.sceneOriginId] = {
        name: scene.sceneOriginName,
        intro: initialSceneIntro[scene.sceneOriginId] ?? '',
      };
    }
    return next;
  }, [scenes, initialSceneIntro]);

  const [selection, setSelection] = useState<Record<string, string>>(buildDefaultSelection);
  const [creatorName, setCreatorName] = useState(seededCreatorName);
  const [projectName, setProjectName] = useState(initialProjectName);
  const [projectDescription, setProjectDescription] = useState(initialProjectDescription);
  const [projectTags, setProjectTags] = useState<string[]>(initialProjectTags);
  const [sceneValues, setSceneValues] = useState<Record<string, SceneCardValues>>(buildDefaultSceneValues);
  const [sceneStatus, setSceneStatus] = useState<Record<string, SceneCardStatus>>({});

  const [fieldErrors, setFieldErrors] = useState<Set<FieldKey>>(new Set());
  const [showSceneErrors, setShowSceneErrors] = useState(false);
  const [visited, setVisited] = useState<Set<ProjectStepId>>(new Set(['select-scenes']));
  const [attempted, setAttempted] = useState<Set<ProjectStepId>>(new Set());

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedNames, setSubmittedNames] = useState<string[]>([]);
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevVisitInputs, setPrevVisitInputs] = useState({ open: false, step: flow.currentStep });

  const selectedScenes = useMemo(
    () => scenes.filter((s) => selection[s.sceneOriginId]).sort((a, b) => a.orderNumber - b.orderNumber),
    [scenes, selection],
  );

  const selectedStoryIds = useMemo(() => selectedScenes.map((s) => s.sceneOriginId), [selectedScenes]);
  const visibilityChoice = useStoryVisibilityChoice(selectedStoryIds, open);

  // Seed on open (not close) so scene lists and project fields that changed
  // while the modal was closed are what the creator sees.
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      flow.reset();
      visibilityChoice.reset();
      setSelection(buildDefaultSelection());
      setCreatorName(seededCreatorName);
      setProjectName(initialProjectName);
      setProjectDescription(initialProjectDescription);
      setProjectTags(initialProjectTags);
      setSceneValues(buildDefaultSceneValues());
      setSceneStatus({});
      setFieldErrors(new Set());
      setShowSceneErrors(false);
      setVisited(new Set(['select-scenes']));
      setAttempted(new Set());
      setSubmitting(false);
      setSubmitError(null);
      setSubmitted(false);
      setSubmittedNames([]);
    }
  }

  if (prevVisitInputs.open !== open || prevVisitInputs.step !== flow.currentStep) {
    setPrevVisitInputs({ open, step: flow.currentStep });
    if (open) {
      setVisited((prev) => (prev.has(flow.currentStep) ? prev : new Set([...prev, flow.currentStep])));
    }
  }

  const firstSelected = selectedScenes[0];
  const projectImages = usePublishFlowImages({
    projectInstanceId,
    sceneOriginId: firstSelected?.sceneOriginId ?? null,
    sceneInstanceId: firstSelected ? selection[firstSelected.sceneOriginId] : null,
    enabled: open,
  });

  const projValues: PublishFlowValues = {
    storyName: '',
    creatorName,
    projectName,
    projectDescription,
    projectTags,
    hasProjectPortrait: Boolean(projectImages.projectPortrait.url),
    hasProjectLandscape: Boolean(projectImages.projectLandscape.url ?? projectImages.sceneLandscape.url),
    hasAnchorImage: true,
    anchorThumbnailUnchanged: false,
    anchorAddress: '',
    // The project flow has no template destination; these keep the shared
    // validation shape satisfied without ever being required by its steps.
    templateName: '',
    templateDescription: '',
    templateLearnings: '',
    templateDifficulty: '',
    templateFeatures: [],
    templateTags: [],
    hasTemplateVideo: false,
    hasTemplateCover: false,
  };

  const sceneValid = useCallback(
    (sceneOriginId: string): boolean => {
      const v = sceneValues[sceneOriginId];
      const status = sceneStatus[sceneOriginId];
      if (!v || !status) return false;
      return isValidStoryName(v.name) && status.landscapeReady && status.anchorReady;
    },
    [sceneValues, sceneStatus],
  );

  const creatorOk = creatorName.trim().length > 0 && !isEmailLike(creatorName);
  const projectInfoMissing = flow.devices.includes('app') ? getMissingFields('project-info', projValues) : [];
  const allScenesValid = selectedScenes.length > 0 && selectedScenes.every((s) => sceneValid(s.sceneOriginId));

  const stepComplete = useCallback(
    (step: ProjectStepId): boolean => {
      switch (step) {
        case 'select-scenes':
          return selectedScenes.length > 0;
        case 'choose-platform':
          return flow.devices.length > 0 && creatorOk;
        case 'project-info':
          return projectInfoMissing.length === 0;
        case 'stories':
          return allScenesValid;
        case 'confirmation':
          return allScenesValid;
      }
    },
    [selectedScenes.length, flow.devices.length, creatorOk, projectInfoMissing.length, allScenesValid],
  );

  const advanceReason = (): string | undefined => {
    switch (flow.currentStep) {
      case 'select-scenes':
        return selectedScenes.length > 0 ? undefined : 'Select at least one story';
      case 'choose-platform':
        if (flow.devices.length === 0) return 'Choose at least one platform';
        if (!creatorOk) return 'Add a creator name';
        return undefined;
      case 'project-info':
        return projectInfoMissing.length > 0 ? 'Complete the project info' : undefined;
      case 'stories':
        return allScenesValid ? undefined : 'Give each story a name and the required images';
      default:
        return undefined;
    }
  };

  const handleNext = () => {
    if (!stepComplete(flow.currentStep)) {
      setAttempted((prev) => new Set(prev).add(flow.currentStep));
      if (flow.currentStep === 'choose-platform') setFieldErrors(creatorOk ? new Set() : new Set(['creatorName']));
      if (flow.currentStep === 'project-info') setFieldErrors(new Set(projectInfoMissing));
      if (flow.currentStep === 'stories') setShowSceneErrors(true);
      return;
    }
    setFieldErrors(new Set());
    flow.goNext();
  };

  const handleBack = () => {
    setFieldErrors(new Set());
    flow.goBack();
  };

  const handleStepClick = (id: ProjectStepId) => {
    setFieldErrors(new Set());
    flow.goToStep(id);
  };

  const toggleScene = (sceneOriginId: string) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[sceneOriginId]) {
        delete next[sceneOriginId];
      } else {
        const scene = scenes.find((s) => s.sceneOriginId === sceneOriginId);
        const v = scene ? latestSelectableVersion(scene) : undefined;
        if (v) next[sceneOriginId] = v;
      }
      return next;
    });
  };

  const selectVersion = (sceneOriginId: string, sceneInstanceId: string) => {
    setSelection((prev) => ({ ...prev, [sceneOriginId]: sceneInstanceId }));
  };

  const handleSceneChange = (sceneOriginId: string, patch: Partial<SceneCardValues>) => {
    setSceneValues((prev) => ({
      ...prev,
      [sceneOriginId]: { ...(prev[sceneOriginId] ?? { name: '', intro: '' }), ...patch },
    }));
  };

  const handleSceneStatus = useCallback((sceneOriginId: string, status: SceneCardStatus) => {
    setSceneStatus((prev) => ({ ...prev, [sceneOriginId]: status }));
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const names = selectedScenes.map((s) => sceneValues[s.sceneOriginId]?.name ?? s.sceneOriginName);
    try {
      await onSubmit({
        devices: flow.devices,
        visibility: visibilityChoice.visibility,
        creatorName,
        ...(flow.devices.includes('app')
          ? { projectName, projectDescription, projectTags }
          : {}),
        scenes: selectedScenes.map((s) => ({
          sceneOriginId: s.sceneOriginId,
          sceneInstanceId: selection[s.sceneOriginId],
          sceneOriginName: s.sceneOriginName,
          storyName: sceneValues[s.sceneOriginId]?.name,
          sceneIntroduction: sceneValues[s.sceneOriginId]?.intro,
        })),
      });
      setSubmittedNames(names);
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const stepNavReason = (target: ProjectStepId): string | undefined => {
    const targetIdx = flow.visibleSteps.indexOf(target);
    if (targetIdx === -1 || targetIdx <= flow.currentStepIndex) return undefined;
    if (!visited.has(target)) return 'Finish the current step first';
    return undefined;
  };

  const body = () => {
    if (submitted) {
      return <ProjectConfirmationStep devices={flow.devices} storyNames={submittedNames} submitted />;
    }
    switch (flow.currentStep) {
      case 'select-scenes':
        return (
          <SelectScenesStep
            scenes={scenes}
            selection={selection}
            onToggleScene={toggleScene}
            onSelectVersion={selectVersion}
            onReorder={onReorderScenes}
          />
        );
      case 'choose-platform':
        return (
          <ChoosePlatformStep
            storyName=""
            creatorName={creatorName}
            anchorCategory={undefined}
            devices={flow.devices}
            errors={fieldErrors}
            versions={[]}
            selectedVersionId={null}
            versionLocked={false}
            showStoryName={false}
            onSelectVersion={() => {}}
            onToggleDevice={flow.toggleDevice}
            visibility={visibilityChoice.visibility}
            onVisibilityChange={visibilityChoice.setVisibility}
            onStoryNameChange={() => {}}
            onCreatorNameChange={(v) => {
              setCreatorName(v);
              setFieldErrors((prev) => withoutKeys(prev, 'creatorName'));
            }}
          />
        );
      case 'project-info':
        return (
          <ProjectInfoStep
            projectName={projectName}
            projectDescription={projectDescription}
            projectTags={projectTags}
            coverImages={projectImages}
            projectInstanceId={projectInstanceId}
            sceneOriginId={firstSelected?.sceneOriginId ?? null}
            errors={fieldErrors}
            onChange={(patch) => {
              if (patch.projectName !== undefined) {
                setProjectName(patch.projectName);
                setFieldErrors((prev) => withoutKeys(prev, 'projectName'));
              }
              if (patch.projectDescription !== undefined) {
                setProjectDescription(patch.projectDescription);
                setFieldErrors((prev) => withoutKeys(prev, 'projectDescription'));
              }
              if (patch.projectTags !== undefined) {
                setProjectTags(patch.projectTags);
                setFieldErrors((prev) => withoutKeys(prev, 'projectTags'));
              }
            }}
          />
        );
      case 'stories':
        return (
          <ProjectStoriesStep
            scenes={selectedScenes.map((s) => ({
              sceneOriginId: s.sceneOriginId,
              sceneInstanceId: selection[s.sceneOriginId],
            }))}
            projectInstanceId={projectInstanceId}
            devices={flow.devices}
            values={sceneValues}
            showErrors={showSceneErrors}
            onChange={handleSceneChange}
            onStatusChange={handleSceneStatus}
          />
        );
      case 'confirmation':
        return (
          <ProjectConfirmationStep
            devices={flow.devices}
            storyNames={selectedScenes.map((s) => sceneValues[s.sceneOriginId]?.name ?? s.sceneOriginName)}
            submitted={submitted}
          />
        );
      default:
        return null;
    }
  };

  const reason = advanceReason();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The surface over a light, blurred scrim, at a fixed height so it keeps its size from step to step */}
      <DialogContent width="xl" scrim="soft" className="h-[min(40rem,calc(100vh-2rem))] max-w-240 bg-lureo-surface" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lureo-16">Publish project</DialogTitle>
        </DialogHeader>

        <StepIndicator<ProjectStepId>
          current={flow.currentStep}
          visibleSteps={flow.visibleSteps}
          onStepClick={handleStepClick}
          disabled={submitting || submitted}
          submitted={submitted}
          allSteps={PROJECT_ALL_STEPS}
          labels={PROJECT_STEP_LABELS}
          getStepDisableReason={stepNavReason}
          getStepSatisfied={(id) => {
            if (id === flow.currentStep) return stepComplete(id);
            return visited.has(id) && stepComplete(id);
          }}
        />

        <DialogBody>
          {!(submitted && flow.currentStep === 'confirmation') && (
            <FlowTitle>
              {STEP_TITLES[flow.currentStep]}
            </FlowTitle>
          )}
          <div className="flex-1 min-h-0">{body()}</div>
        </DialogBody>

        {!submitted && (
          // Back on the left, the way forward on the right, the reason it is blocked between them
          <DialogFooter split>
            {flow.canGoBack ? (
              <LureoButton
                onClick={handleBack}
                variant="outline-2"
                suppressTooltip
                disabled={submitting}
                className="h-9 px-5 rounded-lureo-radius-button-pill"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </LureoButton>
            ) : (
              <span />
            )}

            {submitError ? (
              <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3 truncate">{submitError}</p>
            ) : attempted.has(flow.currentStep) && reason ? (
              <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3 truncate">{reason}</p>
            ) : null}

            {flow.currentStep === 'confirmation' ? (
              <LureoButton
                onClick={handleSubmit}
                variant="secondary"
                suppressTooltip
                disabled={submitting || !allScenesValid}
                className="h-9 px-4 rounded-lureo-radius-button-pill"
              >
                {submitting ? (
                  <LoadingSpinner size="sm" tone="on-cta" className="shrink-0" />
                ) : (
                  <PaperPlaneTiltIcon className="h-4 w-4 shrink-0" />
                )}
                {submitting ? 'Submitting…' : 'Submit for review'}
              </LureoButton>
            ) : (
              <LureoButton
                onClick={handleNext}
                variant="secondary"
                suppressTooltip
                disabled={submitting}
                className="h-9 px-4 rounded-lureo-radius-button-pill"
              >
                Next
                <ArrowRightIcon className="h-4 w-4" />
              </LureoButton>
            )}
          </DialogFooter>
        )}

        {submitted && (
          <DialogFooter>
            <LureoButton
              onClick={() => onOpenChange(false)}
              variant="secondary"
              suppressTooltip
              className="h-9 px-4 rounded-lureo-radius-button-pill"
            >
              Done
            </LureoButton>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
